import type { CSSProperties } from 'react';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { absolutePhotoUrl, applyRenditionIfMediaServiceUrl } from './mediaService';
import type { Rendition } from './mediaService';

/**
 * Colorway live-preview helpers.
 *
 * A colorway template carries, on the ThemeJson, a `colorways` object with an
 * `options` array of `{ id, values }` entries, each `values` an ordered
 * `string[]`. Layer color/src fields reference a `values` entry by index via a
 * `var(--N)` token; the color values make up the swatch. FE preview
 * surfaces (gallery tile, PDP, editor
 * canvas) render those tokens by setting the selected colorway's values as
 * inline CSS custom properties (`--N`) on an ancestor element —
 * non-destructive, so the draft keeps its tokens for the backend to collapse
 * at render time. See `useColorways`.
 */

const VAR_RE = /^var\(\s*--(\d+)\s*\)$/;

/**
 * Composes the theme-search embeddings-index row id for a colorway variant.
 * The index (themes.json/themes.bin) keys each colorway variant as
 * `${themeId}_${colorwayName}` (lowercase snake_case), e.g. `boldly_tomato`,
 * `emilio_hotels_blush`. Composition is deterministic even though themeIds
 * themselves contain underscores — we always join, never split.
 */
export function colorwayEmbeddingKey(themeId: string, colorwayId: string): string {
  return `${themeId}_${colorwayId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
}

/**
 * Colorway `values` mix colors and image references — image values are
 * media-service filenames while colors are always `rgb(...)`/`rgba(...)`. Only
 * the colors reach the swatch. See `swatchColorsByColorway`.
 */
const COLOR_VALUE_RE = /^rgba?\(/;

/**
 * Relative luminance (0 = black, 1 = white) of an `rgb()`/`rgba()` string,
 * or `undefined` if the channels can't be read. Uses the sRGB coefficients
 * rather than a plain average so yellows aren't mistaken for dark.
 */
function luminance(color: string): number | undefined {
  const channels = color.match(/[\d.]+/g);
  if (!channels || channels.length < 3) return undefined;
  const [r, g, b] = channels.slice(0, 3).map(Number);
  if ([r, g, b].some(isNaN)) return undefined;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/**
 * Above this luminance a color is a candidate for dropping: white and ivory
 * card backgrounds (~0.97+) but also the pastel tints that pair with a
 * saturated color (pale pink ~0.92, pale sky blue ~0.93, pale green ~0.95).
 * Kept below those so the pastel cases clear it with margin.
 */
const PALE_LUMINANCE = 0.82;

/**
 * How much darker a sibling must be for a pale color to count as redundant.
 * Guards against dropping a color whose partners are barely darker — e.g. two
 * ivories, where neither is the "real" color of the pair.
 */
const PALE_CONTRAST_GAP = 0.25;

const isPale = (color: string) => {
  const value = luminance(color);
  return value !== undefined && value >= PALE_LUMINANCE;
};

/**
 * The color values of one colorway, keyed by their original index so the same
 * `var(--N)` slot lines up across colorways. Image references are omitted
 * rather than compacted away, which would shift later colors into the wrong
 * slot.
 */
function colorValuesByIndex(values: string[] | undefined): Map<number, string> {
  const result = new Map<number, string>();
  values?.forEach((value, index) => {
    if (COLOR_VALUE_RE.test(value)) result.set(index, value);
  });
  return result;
}

/** Every readable luminance across a set of colorways, ignoring unparseable values. */
function allLuminances(colorsByIndex: ReadonlyArray<Map<number, string>>): number[] {
  return colorsByIndex.flatMap(colors => [...colors.values()].map(luminance).filter((value): value is number => value !== undefined));
}

/**
 * Which `var(--N)` indices hold a pale color in *every* colorway that defines
 * them, and so can be dropped from all swatches.
 *
 * Pale-ness is judged per index rather than per colorway: an index whose colors
 * are pale everywhere is a shared background tint that distinguishes nothing,
 * while an index that is pale in some colorways and dark in others is exactly
 * what tells them apart. A white-on-black and black-on-white pair, for example,
 * has a pale color in each colorway but no uniformly pale index — so it keeps
 * both colors and the two swatches stay distinct.
 *
 * An index only qualifies if some colorway also has a substantially darker
 * color to stand in for the dropped tint.
 */
function redundantPaleIndices(colorsByIndex: ReadonlyArray<Map<number, string>>): Set<number> {
  const indices = new Set<number>();
  for (const colors of colorsByIndex) for (const index of colors.keys()) indices.add(index);
  // Nothing is worth dropping unless a genuinely darker color exists somewhere
  // in the set to carry the swatch — e.g. a set of ivories keeps its tints.
  // Measured across the whole set rather than per colorway, so one colorway
  // whose accent is only mid-toned cannot veto the reduction for the others.
  const darkest = Math.min(...allLuminances(colorsByIndex));
  if (!isFinite(darkest) || darkest > PALE_LUMINANCE - PALE_CONTRAST_GAP) return new Set();
  const redundant = new Set<number>();
  for (const index of indices) {
    const atIndex = colorsByIndex.map(colors => colors.get(index)).filter((color): color is string => color !== undefined);
    if (atIndex.length > 0 && atIndex.every(isPale)) redundant.add(index);
  }
  return redundant;
}

/**
 * Picks the swatch colors for every colorway in a set, as a `[colorwayId,
 * colors]` map.
 *
 * The swatch exists to tell colorways *apart*, not to reproduce the card, so it
 * may render darker than the card it stands for. Pale tints are dropped only
 * where they carry no distinguishing information, decided per `var(--N)` index
 * across the whole set:
 *
 * - `[pale-pink, red]`, `[pale-blue, blue]`, `[pale-green, green]` → index 0 is
 *   pale in every colorway, so it is dropped and each swatch shows just red,
 *   blue, green.
 * - add `[dark-grey, black]` and index 0 is no longer uniformly pale, so every
 *   color is shown.
 * - `[white, black]` and `[black, white]` keep both colors: each colorway has a
 *   pale color, but neither index is pale throughout, so dropping either would
 *   make the two swatches identical.
 *
 * A colorway with no color values maps to `['transparent']`.
 */
export function swatchColorsByColorway(options: ReadonlyArray<{ id: string; values: string[] }>): Map<string, string[]> {
  const colorsById = new Map(options.map(option => [option.id, colorValuesByIndex(option.values)]));
  const redundant = redundantPaleIndices([...colorsById.values()].filter(colors => colors.size > 0));
  const result = new Map<string, string[]>();
  for (const [id, colors] of colorsById) {
    if (colors.size === 0) {
      result.set(id, ['transparent']);
      continue;
    }
    const all = [...colors.entries()].sort(([a], [b]) => a - b);
    const kept = all.filter(([index]) => !redundant.has(index)).map(([, color]) => color);
    result.set(id, kept.length > 0 ? kept : all.map(([, color]) => color));
  }
  return result;
}

/**
 * Colorway arrays carry no `--image`/`--color` name prefix to key off, so we
 * determine which indices hold image references by scanning the page's layers:
 * `var(--N)` on an image's `src` marks index N as an image.
 */
export function imageVarIndices(layers: ReadonlyArray<LayerData>): Set<number> {
  const out = new Set<number>();
  for (const layer of layers) {
    const src = layer.type === 'image' ? layer.imageData.src : undefined;
    const match = src?.match(VAR_RE);
    if (match) out.add(Number(match[1]));
  }
  return out;
}

/** Builds inline CSS custom properties (`--N`) for the selected colorway's values. */
export function buildCssVarStyle(values: string[], imageIndices: Set<number>, rendition?: Rendition): CSSProperties {
  const result: Record<string, string> = {};
  values.forEach((value, index) => {
    if (imageIndices.has(index)) {
      const fullUrl = absolutePhotoUrl(value);
      result[`--${index}`] = `url(${applyRenditionIfMediaServiceUrl(fullUrl, rendition)})`;
    } else {
      result[`--${index}`] = value;
    }
  });
  return result as CSSProperties;
}
